import { useDispatch } from 'react-redux'; // Importing Redux hooks to interact with the store
import { useTranslation } from 'react-i18next'; // Importing the translation hook to handle multi-language support
import { useEffect, useState } from 'react'; // Importing React hooks for side-effects and state management
import { useNavigate } from 'react-router-dom'; // Importing the navigation hook for routing
import { GetUserByIdAtEntry } from '../../../../../reduxToolkit/authentication/AuthenticatonApi'; // Fetch user data on entry
import { toast } from 'react-toastify'; // Importing toast notifications
import { cancelEventByList,
    deleteEvents,
} from '../../../../../reduxToolkit/EmployeeEvents/EmployeeEventsApi'; // Actions for event operations
import { clearSearchFilters } from '../../../../../reduxToolkit/Search/SearchSlice'; // Action to clear search filters
import routes from '../../../../../Routes/routes'; // Importing routing paths for navigation
import { errorMsg } from '../../../../../Apis/functions';
import React, { useImperativeHandle } from 'react';
import {ACTION_MODEL} from '../../../../../Helpers/ActionModel';

/**
 * @Author: Cecilia López Martínez
 * @JobTitle: Full Stack Developer - IBL
 * @Email: clopez@ibl.mx
 */
const EventsApp = ({ ref, setSelectedList, selectedList, option, moduleId }) => {

    const { t } = useTranslation(); // Initialize translation function
    const dispatch = useDispatch(); // Initialize dispatch function for sending actions to Redux store
    const navigate = useNavigate(); // Initialize navigate function for routing
    // Local state variables for managing events and UI components
    const [isAllChecked, setIsAllChecked] = useState(false); // State to manage 'select all' checkbox for events
    const [showCancelModal, setShowCancelModal] = useState(false); // State for showing cancel modal
    const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing delete modal
    const [results, setResults] = useState(null); // State to store the search results
    const [userId, setUserId] = useState(null); // State to store user ID
    const [modified, setModified] = useState(0);

    /**
     * Effect to fetch user data on component mount
     */
    useEffect(() => {
        dispatch(GetUserByIdAtEntry()).then(({ payload: { data: { data, success, code } } }) => {
            if (success) {
                setUserId(data.userId);
            } else {
                errorMsg(code);
            }
        });
    }, []);

    /**
     * Effect hook to reset states and clear search filters on component unmount
     */
    useEffect(() => {
        return () => {
            dispatch(clearSearchFilters()); // Clear search filters when component unmounts
        };
    }, []);

    useImperativeHandle(ref, () => ({
        handleShowModals,
    }));


    /**
     * Function to show the cancel and delete modal
     */
    const handleShowModals = (action, event) => {
        if(event !== null && typeof event !== 'undefined' ){
            setSelectedList([event.id]);
        }
        const modalActions = {
            [ACTION_MODEL.DELETE]: () => setShowDeleteModal(true),
            [ACTION_MODEL.CANCEL]: () => setShowCancelModal(true),
        };
        modalActions[action]?.();
    };


    /**
     *  Function to hide the cancel and delete modal
     */
    const handleHideModals = () => {
        setIsAllChecked(false);
        setSelectedList([]);
        setShowCancelModal(false);
        setShowDeleteModal(false);
    };

    /**
     * Function to handle event creation, redirecting to different lists based on restrictions
     */
    const handleCreateEventOnTable = () => {
        navigate(routes.event.create);
    };

    /**
     * Function to handle event delete, validate list by userId
     */
    const handleDeleteEvent = () => {
        const isDifferentUser = results?.content?.
            filter(item => selectedList?.includes(item.id) && item.hostId !== userId).
            map(item => ({ id: item.id, name: item.name }));

        if (isDifferentUser?.length > 0) {
            toast.error(t('event_deletion_restriction')); // Show error if deletion is restricted
            setShowDeleteModal(false); // Close the delete modal
            setIsAllChecked(false);
            setSelectedList([]);
            return;
        }

        if (selectedList?.length > 0) {
            apiDeleteByList(selectedList);
        }
    };

    /**
     *  Handle to event cancel by list
     */
    const handleCancelEventByList = () => {
        dispatch(cancelEventByList(selectedList)).then(({ payload: { data: { success, code } } }) => {
            if (success === true) {
                setShowCancelModal(false);
                setModified(prev => prev + 1);
                setIsAllChecked(false);
                setSelectedList([]);
                toast.success(t('has_been_cancelled_successfully'));
            } else {
                errorMsg(code);
            }
        });
    };

    /**
     * Handle to delete event by list
     */
    const apiDeleteByList = (list) => {
        dispatch(deleteEvents(list)).then(({ payload: { data: { success, code } } }) => {
            if (success === true) {
                setShowDeleteModal(false); // Close the delete modal after cancellation
                setModified(prev => prev + 1);
                setIsAllChecked(false);
                setSelectedList([]);
                toast.success(t('has_been_deleted_successfully')); // Show success toast
            } else {
                errorMsg(code);
            }
        });
    };

    return {
        t,
        modified,
        setModified,
        isAllChecked,
        setIsAllChecked,
        showCancelModal,
        showDeleteModal,
        handleHideModals,
        handleCancelEventByList,
        handleShowModals,
        handleCreateEventOnTable,
        handleDeleteEvent,
        results,
        setResults
    };
};

export default EventsApp;
